import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { getTourState, setTourState, resetTour, markStepCompleted, TourState } from '@/services/tourService';

export interface TourStep {
  id: string;
  route: string;
  selector: string;
  title: string;
  description: string;
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'bottom-left';
  primaryLabel: string;
  primaryAction?: () => void;
  fallbackMessage?: string;
  cursorTarget?: string;
  cursorAction?: 'click' | 'hover' | 'move';
  cursorDelay?: number;
  onCursorComplete?: () => void;
  postActionSelector?: string;
  postActionPlacement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  fixedPosition?: { top: number; left: number };
  postActionFixedPosition?: { top: number; left: number } | null;
  autoClickTarget?: boolean;
}

interface TourContextValue {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  steps: TourStep[];
  tourState: TourState;
  startTour: () => void;
  stopTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  resetAndRestart: () => void;
  showBadge: boolean;
  actionFired: boolean;
  fireAction: () => void;
  stepRunId: string;
  transitioning: boolean;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour() {
  useLanguage();
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error(translateInterfaceText("useTour must be inside TourProvider"));
  return ctx;
}

function cleanupStepUI() {
  window.dispatchEvent(new CustomEvent('tour:cleanup'));
  document.body.click();
  const escEvent = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true });
  document.dispatchEvent(escEvent);
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourState, setTourStateLocal] = useState<TourState>(getTourState);
  const [actionFired, setActionFired] = useState(false);
  const [stepRunId, setStepRunId] = useState(() => nanoid());
  const [transitioning, setTransitioning] = useState(false);
  const navigationPendingRef = useRef(false);
  const lockRef = useRef(false);

  const steps: TourStep[] = [
    // STEP 01 — Adicionar vídeo
    {
      id: 'add_video',
      route: '/dashboard',
      selector: '[data-tour="videos-add"]',
      title: translateInterfaceText("Adicione um vídeo"),
      description: translateInterfaceText("Clique no botão azul para abrir o upload e enviar seu primeiro vídeo."),
      placement: 'bottom',
      primaryLabel: translateInterfaceText("Adicionar vídeo"),
      fixedPosition: { top: 80, left: 24 },
      postActionFixedPosition: { top: 80, left: 24 },
      primaryAction: () => {
        window.dispatchEvent(new CustomEvent('tour:open-upload'));
      },
    },
    // STEP 02 — Criar pasta
    {
      id: 'create_folder',
      route: '/dashboard',
      selector: '[data-tour="modal-folder-create"]',
      title: translateInterfaceText("Crie uma pasta"),
      description: translateInterfaceText("Use pastas para organizar suas VSLs por produto ou campanha."),
      placement: 'right',
      primaryLabel: translateInterfaceText("Próximo"),
      fixedPosition: { top: 80, left: 24 },
      postActionFixedPosition: { top: 80, left: 24 },
      cursorTarget: '[data-tour="folder-new"]',
      cursorAction: 'move',
      cursorDelay: 500,
      primaryAction: () => {
        window.dispatchEvent(new CustomEvent('tour:open-create-folder'));
      },
    },
    // STEP 03 — Abrir menu do vídeo
    {
      id: 'open_video_menu',
      route: '/dashboard',
      selector: '[data-tour="video-actions-btn"]',
      title: translateInterfaceText("Ações do vídeo"),
      description: translateInterfaceText("Vou abrir o menu do vídeo para você ver as opções disponíveis."),
      placement: 'left',
      primaryLabel: translateInterfaceText("Próximo"),
      fixedPosition: { top: 80, left: 24 },
      autoClickTarget: true,
      cursorTarget: '[data-tour="video-actions-btn"]',
      cursorAction: 'click',
      cursorDelay: 600,
      postActionSelector: '[data-tour="video-actions-menu"]',
      postActionPlacement: 'left',
      postActionFixedPosition: { top: 80, left: 24 },
    },
    // STEP 04 — Personalização
    {
      id: 'personalize',
      route: '/personalizations',
      selector: '[data-tour="sidebar-controls"]',
      title: translateInterfaceText("Recursos do player"),
      description: translateInterfaceText("Aqui ficam todos os recursos para personalizar seu player. Ative controles, botões, headline e muito mais."),
      placement: 'right',
      primaryLabel: translateInterfaceText("Próximo"),
      fixedPosition: { top: 80, left: 24 },
      fallbackMessage: translateInterfaceText("Explore os recursos na barra lateral para personalizar seu player."),
    },
    // STEP 05 — Salvar e copiar código
    {
      id: 'save_and_copy',
      route: '/personalizations',
      selector: '[data-tour="btn-save"]',
      title: translateInterfaceText("Salvar e copiar código"),
      description: translateInterfaceText("Salve suas alterações e depois copie o código para colar na sua página de vendas."),
      placement: 'bottom',
      primaryLabel: translateInterfaceText("Próximo"),
      fallbackMessage: translateInterfaceText("Salve suas alterações primeiro, depois copie o código de incorporação."),
    },
    // STEP 06 — Final
    {
      id: 'final',
      route: '/personalizations',
      selector: '[data-tour="btn-copy-code"]',
      title: translateInterfaceText("Pronto! Agora é só colar 🎉"),
      description: translateInterfaceText("Cole o código em um bloco HTML da sua página de vendas. Publique a página e pronto: seu vídeo já estará no ar."),
      placement: 'center',
      primaryLabel: translateInterfaceText("Fechar"),
    },
  ];

  const totalSteps = steps.length;
  const showBadge = tourState.status !== 'completed';

  const trackEvent = useCallback((event: string, payload?: any) => {
    console.log('[Tour]', event, payload);
  }, []);

  const fireAction = useCallback(() => {
    setActionFired(true);
  }, []);

  const startTour = useCallback(() => {
    if (lockRef.current) return;
    lockRef.current = true;

    cleanupStepUI();

    setTimeout(() => {
      setIsActive(true);
      setCurrentStep(0);
      setActionFired(false);
      setStepRunId(nanoid());
      setTransitioning(false);
      const newState = setTourState({ status: 'in_progress', lastStep: 0 });
      setTourStateLocal(newState);
      trackEvent('tour_firststeps_started');

      if (location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }

      setTimeout(() => { lockRef.current = false; }, 500);
    }, 100);
  }, [navigate, location.pathname, trackEvent]);

  const stopTour = useCallback(() => {
    setIsActive(false);
    setActionFired(false);
    setStepRunId(nanoid());
    const newState = setTourState({ status: 'dismissed' });
    setTourStateLocal(newState);
    trackEvent('tour_firststeps_dismissed');
    cleanupStepUI();
  }, [trackEvent]);

  const completeTour = useCallback(() => {
    setIsActive(false);
    setActionFired(false);
    setStepRunId(nanoid());
    const newState = setTourState({
      status: 'completed',
      completedAt: new Date().toISOString(),
      lastStep: totalSteps - 1,
    });
    setTourStateLocal(newState);
    trackEvent('tour_firststeps_completed');
    cleanupStepUI();
  }, [totalSteps, trackEvent]);

  const goToStep = useCallback((index: number) => {
    if (index < 0 || index >= totalSteps) return;
    if (transitioning) return;

    setTransitioning(true);
    const targetStep = steps[index];
    const curPath = location.pathname;
    const needsNav = targetStep.route === '/personalizations'
      ? !curPath.startsWith('/personalizations')
      : targetStep.route === '/dashboard'
      ? curPath !== '/dashboard'
      : curPath !== targetStep.route;
    setTimeout(() => setTransitioning(false), needsNav ? 600 : 250);

    const newRunId = nanoid();
    setStepRunId(newRunId);

    const step = steps[index];

    cleanupStepUI();

    setCurrentStep(index);
    setActionFired(false);
    const newState = setTourState({ lastStep: index });
    setTourStateLocal(newState);
    markStepCompleted(index);
    trackEvent('tour_firststeps_step_viewed', { step_id: step.id, step_index: index });

    if ((step.id === 'create_folder' || step.id === 'add_video') && step.primaryAction) {
      setTimeout(() => {
        step.primaryAction?.();
        setActionFired(true);
      }, 400);
    }

    const currentPath = location.pathname;
    const needsNavigation = step.route === '/personalizations'
      ? !currentPath.startsWith('/personalizations')
      : step.route === '/dashboard'
      ? currentPath !== '/dashboard'
      : currentPath !== step.route;

    if (needsNavigation) {
      navigationPendingRef.current = true;
      navigate(step.route);
    }
  }, [totalSteps, steps, location.pathname, navigate, trackEvent, transitioning]);

  const nextStep = useCallback(() => {
    if (transitioning) return;
    if (currentStep >= totalSteps - 1) {
      completeTour();
      return;
    }
    goToStep(currentStep + 1);
  }, [currentStep, totalSteps, goToStep, completeTour, transitioning]);

  const prevStep = useCallback(() => {
    if (transitioning) return;
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep, transitioning]);

  const resetAndRestart = useCallback(() => {
    const newState = resetTour();
    setTourStateLocal(newState);
    startTour();
  }, [startTour]);

  useEffect(() => {
    if (!isActive) return;
    const step = steps[currentStep];
    if (!step?.selector || step.placement === 'center') return;

    const timeout = setTimeout(() => {
      const el = document.querySelector(step.selector);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [isActive, currentStep, steps, location.pathname]);

  return (
    <TourContext.Provider value={{
      isActive,
      currentStep,
      totalSteps,
      steps,
      tourState,
      startTour,
      stopTour,
      nextStep,
      prevStep,
      goToStep,
      resetAndRestart,
      showBadge,
      actionFired,
      fireAction,
      stepRunId,
      transitioning,
    }}>
      {children}
    </TourContext.Provider>
  );
}
